import { zodResolver } from "@hookform/resolvers/zod";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { createTentativeIncome } from "web_myplann/Finances/Helpers/createTentativeIncome";
import { getCollapsedEntryId } from "web_myplann/Finances/Helpers/getCollapsedEntryId";
import { isDetailedIncome } from "web_myplann/Finances/Helpers/isDetailedIncome";
import { financeState } from "web_myplann/Finances/State/FinanceAtom";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { z } from "zod";

const getIncomeFormSchema = (t: TranslateFunction) =>
	z.object({
		income: z.array(
			z.object({
				id: z.number().nullable(),
				name: z.string({
					required_error: t(Resources.Form.Validations.required),
				}),
				amount: z.string({
					required_error: t(Resources.Form.Validations.required),
				}),
			}),
		),
		totalValue: z.number().optional(),
		exact: z.boolean().optional(),
	});

type IncomeFormModel = z.infer<ReturnType<typeof getIncomeFormSchema>>;

export const useIncomeForm = () => {
	const { t } = useResource();

	const [finance, setFinance] = useAtom(financeState);

	const isExact = isDetailedIncome(finance?.income ?? []);

	const form = useForm<IncomeFormModel>({
		resolver: zodResolver(getIncomeFormSchema(t)),
		values: {
			income:
				finance?.income?.map((e) => ({
					id: e.id,
					name: e.name ?? "",
					amount: String(e.amount),
				})) ?? [],
			totalValue: finance?.income?.reduce((acc, curr) => acc + curr.amount, 0),
			exact: isExact,
		},
	});

	const { control, setValue, watch, handleSubmit, reset } = form;

	useEffect(() => {
		if (finance?.income) {
			reset({
				income: finance.income.map((e) => ({
					id: e.id,
					name: e.name ?? "",
					amount: e.amount.toString(),
				})),
				exact: isExact,
			});
		}
	}, [finance?.income, reset]);

	const incomes = useWatch({
		control,
		name: "income",
	});

	const exact = useWatch({
		control,
		name: "exact",
	});

	useEffect(() => {
		const sum =
			(incomes ?? []).reduce((acc, curr) => {
				const n = Number.parseFloat(
					String(curr?.amount ?? "0").replace(",", "."),
				);
				return acc + (Number.isNaN(n) ? 0 : n);
			}, 0) ?? 0;
		setValue("totalValue", sum);
	}, [incomes, setValue, exact]);

	const {
		fields: incomeFields,
		append: incomeAppend,
		remove: incomeRemove,
	} = useFieldArray({
		control,
		name: "income",
	});

	const onSubmit = handleSubmit((data) => {
		if (!exact) {
			setFinance({
				...finance,
				income: [
					createTentativeIncome(
						getCollapsedEntryId(finance?.income ?? []),
						data.totalValue ?? 0,
						t,
					),
				],
			});
			return;
		}
		setFinance({
			...finance,
			income: data.income.map((e) => ({
				id: e.id,
				name: e.name,
				amount: Number(e.amount),
			})),
		});
	});

	return {
		incomeFields,
		incomeAppend,
		incomeRemove,
		setValue,
		watch,
		onSubmit,
		control,
	};
};
